import type { WorkOrder, WorkOrderBase, ChecklistTemplate, ChecklistExecution } from "../../types";
import type { IOperationResult } from "../types";
import { createBaseService } from "./baseService";
import { PmScheduleService } from "./pmScheduleService";
import { ChecklistService } from "./checklistService";

const base = createBaseService<WorkOrder, WorkOrderBase>({
  tableName: "work_orders",
  viewName: "work_orders_enriched",
  searchColumns: ["title"],
  computedColumns: ["assetName", "locationName", "pmName", "approvedByName"],
});

export class WorkOrderService {
  static getAll = base.getAll;
  static get = base.get;
  static create = base.create;
  static update = base.update;
  static delete = base.delete;

  static async generateDueFromPmSchedules(now = new Date()): Promise<
    IOperationResult<{ created: number; skipped: number; total: number }>
  > {
    try {
      const pmResult = await PmScheduleService.getAll();
      if (!pmResult.success) return { success: false, error: pmResult.error };

      const pms = pmResult.data ?? [];
      const templateResult = await ChecklistService.getAll();
      const templateMap = new Map<string, ChecklistTemplate>(
        (templateResult.data ?? []).map((t) => [t.id, t])
      );

      let created = 0;
      let skipped = 0;

      for (const pm of pms) {
        if (pm.status !== "active" || new Date(pm.nextDueDate) > now) {
          skipped++;
          continue;
        }

        // Check if WO already exists for this PM + due date
        const existing = await this.getAll({
          search: pm.name,
        });
        const dueDateStr = pm.nextDueDate.slice(0, 10);
        const alreadyExists = (existing.data ?? []).some(
          (wo) => wo.pmId === pm.id && wo.dueDate?.slice(0, 10) === dueDateStr
        );
        if (alreadyExists) {
          skipped++;
          continue;
        }

        const checklistTemplate = pm.checklistTemplateId
          ? templateMap.get(pm.checklistTemplateId)
          : undefined;
        const checklists: ChecklistExecution[] = checklistTemplate
          ? [{
              templateId: checklistTemplate.id,
              templateName: checklistTemplate.name,
              items: checklistTemplate.items.map((item) => ({ ...item })),
            }]
          : [];

        await this.create({
          title: pm.name,
          description: pm.description || `Generated from PM schedule: ${pm.name}`,
          type: "preventive",
          priority: pm.workOrderPriority ?? "medium",
          status: "open",
          assetId: pm.assetId,
          locationId: pm.locationId,
          pmId: pm.id,
          assignees: pm.assignees,
          dueDate: pm.nextDueDate,
          checklists,
        });
        created++;
      }

      return {
        success: true,
        data: { created, skipped, total: pms.length },
      };
    } catch (error) {
      return { success: false, error };
    }
  }
}
